import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  useTheme,
  CircularProgress,
  Pagination,
  FormControlLabel,
  Switch,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import { StyledButton } from '../../../components';
import useObserveTripSheets from '../../../hooks/useSubscribeTripsheet';
import { useCurrentUser, useDatastore } from '../../../hooks';
import { useAlert } from '../../../hooks';
import dayjs from 'dayjs';
import { completeManifest, deleteManifest } from '../../../services/adminManifestActions';
import { logAdminAction, AdminActionType } from '../../../services/auditLogger';

interface Manifest {
  id: string;
  tripId: string;
  tripReference: string;
  tripSheetId: string;
  legId: string;
  status: string;
  ssccs: string[];
  asns: string[];
  createdAt: string;
  destination: string;
  isDeleted: boolean;
}

export default function ManifestManagement() {
  const theme = useTheme();
  const user = useCurrentUser();
  const alert = useAlert();
  const tripSheets = useObserveTripSheets();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('trip') || '');
  const [manifestSearch, setManifestSearch] = useState(searchParams.get('manifest') || '');
  const [asnSearch, setAsnSearch] = useState('');
  const [ssccSearch, setSsccSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedManifest, setSelectedManifest] = useState<Manifest | null>(null);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [hideEmptyTrips, setHideEmptyTrips] = useState(true);
  const [validationError, setValidationError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [openAddSSCCDialog, setOpenAddSSCCDialog] = useState(false);
  const [selectedSSCC, setSelectedSSCC] = useState<string>('');
  const [addSSCCReason, setAddSSCCReason] = useState('');
  const [availableSSCCs, setAvailableSSCCs] = useState<any[]>([]);
  const [openBustleDialog, setOpenBustleDialog] = useState(false);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const store = useDatastore();

  // Update URL when manifest search changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (manifestSearch) {
      params.set('manifest', manifestSearch);
    } else {
      params.delete('manifest');
    }
    if (searchQuery) {
      params.set('trip', searchQuery);
    } else {
      params.delete('trip');
    }
    setSearchParams(params, { replace: true });
  }, [manifestSearch, searchQuery]);

  // Debug: Log tripsheets data for troubleshooting
  console.log('TripSheets:', tripSheets.length, tripSheets);

  // Detect duplicate manifests across all trips (must be before filters that use it)
  const manifestOccurrences = new Map<string, number>();
  tripSheets.forEach((trip) => {
    trip.legs.forEach((leg) => {
      leg.manifests.forEach((manifest) => {
        const count = manifestOccurrences.get(manifest.id) || 0;
        manifestOccurrences.set(manifest.id, count + 1);
      });
    });
  });

  // Filter tripsheets based on search query and date
  let filteredTripSheets = tripSheets;

  // Filter by trip reference search query
  if (searchQuery.trim()) {
    filteredTripSheets = filteredTripSheets.filter((trip) =>
      trip.reference?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  // Filter by manifest number
  if (manifestSearch.trim()) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      return trip.legs.some((leg) =>
        leg.manifests.some((manifest) => manifest.id.toLowerCase().includes(manifestSearch.toLowerCase()))
      );
    });
  }

  // Filter by ASN
  if (asnSearch.trim()) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      return trip.legs.some((leg) =>
        leg.manifests.some((manifest) =>
          manifest.ssccs.some((sscc: any) =>
            sscc.packages.some((pkg: any) => pkg.asn?.toLowerCase().includes(asnSearch.toLowerCase()))
          )
        )
      );
    });
  }

  // Filter by SSCC
  if (ssccSearch.trim()) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      return trip.legs.some((leg) =>
        leg.manifests.some((manifest) =>
          manifest.ssccs.some((sscc: any) => sscc.ssccId?.toLowerCase().includes(ssccSearch.toLowerCase()))
        )
      );
    });
  }

  // Filter by date
  if (selectedDate) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      if (!trip.createdAt) return false;
      const tripDate = dayjs(trip.createdAt).format('YYYY-MM-DD');
      return tripDate === selectedDate;
    });
  }

  // Filter out trips with no manifests if toggle is active
  if (hideEmptyTrips) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      return trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) > 0;
    });
  }

  // Filter to show only trips with duplicate manifests if toggle is active
  if (showOnlyDuplicates) {
    filteredTripSheets = filteredTripSheets.filter((trip) => {
      return trip.legs.some((leg) => leg.manifests.some((manifest) => manifestOccurrences.get(manifest.id)! > 1));
    });
  }

  console.log('Filtered TripSheets:', filteredTripSheets.length);

  // Pagination logic
  const pageSize = 10;
  const totalPages = Math.ceil(filteredTripSheets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredTripSheets.length);
  const paginatedTripSheets = filteredTripSheets.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleManifestSearchChange = (value: string) => {
    setManifestSearch(value);
    setCurrentPage(1);
  };

  const handleAsnSearchChange = (value: string) => {
    setAsnSearch(value);
    setCurrentPage(1);
  };

  const handleSsccSearchChange = (value: string) => {
    setSsccSearch(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Transform tripsheet data to include manifests with extracted SSCCs and ASNs
  const transformManifest = (manifest: any, trip: any, leg: any): Manifest => {
    const ssccIds = manifest.ssccs.map((sscc: any) => sscc.ssccId);

    const asnSet = new Set<string>();
    manifest.ssccs.forEach((sscc: any) => {
      sscc.packages.forEach((pkg: any) => {
        if (pkg.asn) {
          asnSet.add(pkg.asn);
        }
      });
    });
    const asns = Array.from(asnSet);

    return {
      id: manifest.id,
      tripId: manifest.tripId,
      tripSheetId: trip.id,
      legId: leg.id,
      tripReference: trip.reference || '',
      status: manifest.status.toString(),
      ssccs: ssccIds,
      asns: asns,
      createdAt: trip.createdAt ? dayjs(trip.createdAt).format('YYYY-MM-DD') : '',
      destination: leg.destination.businessName,
      isDeleted: false
    };
  };

  const handleMarkAsCompleted = async () => {
    if (!selectedManifest || !actionReason.trim() || isProcessing) return;

    try {
      setIsProcessing(true);

      const result = await completeManifest({
        tripSheetId: selectedManifest.tripSheetId,
        manifestId: selectedManifest.id,
        reason: actionReason,
        actionBy: user.email
      });

      if (result.success) {
        alert(result.message, 'success');
        setOpenCompleteDialog(false);
        setSelectedManifest(null);
        setActionReason('');
      } else {
        alert(result.message, 'error');
      }
    } catch (error) {
      console.error('Failed to mark manifest as completed:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteManifest = async () => {
    if (!selectedManifest || !actionReason.trim() || isProcessing) return;

    try {
      setIsProcessing(true);
      setValidationError('');

      const result = await deleteManifest({
        tripSheetId: selectedManifest.tripSheetId,
        manifestId: selectedManifest.id,
        legId: selectedManifest.legId,
        reason: actionReason,
        actionBy: user.email
      });

      if (result.success) {
        alert(result.message, 'success');
        setOpenDeleteDialog(false);
        setSelectedManifest(null);
        setActionReason('');
        setValidationError('');
      } else {
        setValidationError(result.message);
      }
    } catch (error) {
      console.error('Failed to delete manifest:', error);
      setValidationError('An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenAddSSCCDialog = async (manifest: Manifest) => {
    setSelectedManifest(manifest);
    setOpenAddSSCCDialog(true);
    // Load available SSCCs that are not already in this manifest
    try {
      const allSSCCs = await store.ssccs.getList('', undefined, 'open', true);
      if (allSSCCs) {
        // Filter out SSCCs already in the manifest
        const filteredSSCCs = allSSCCs.filter((sscc) => !manifest.ssccs.includes(sscc.id));

        // Fetch destination locations for each SSCC
        const ssccsWithLocations = await Promise.all(
          filteredSSCCs.map(async (s) => {
            const destinationLocation = s.destinationLocationId
              ? await store.locations.getById(s.destinationLocationId)
              : undefined;
            return {
              ...s,
              destinationLocation
            };
          })
        );

        setAvailableSSCCs(ssccsWithLocations);
      }
    } catch (error) {
      console.error('Error loading SSCCs:', error);
    }
  };

  const handleAddSSCCToManifest = async () => {
    if (!selectedManifest || !selectedSSCC || !addSSCCReason.trim() || isProcessing) return;

    try {
      setIsProcessing(true);

      // Get the SSCC data
      const ssccData = await store.ssccs.get(selectedSSCC);
      if (!ssccData) {
        alert('SSCC not found', 'error');
        return;
      }

      // Capture before state for audit
      const beforeState = {
        manifestId: selectedManifest.id,
        ssccId: selectedSSCC,
        ssccStatus: ssccData.status,
        asnStatuses: ssccData.receiptedEvents.flatMap((event: any) =>
          event.packages.map((pkg: any) => ({
            asn: pkg.asn,
            status: pkg.status
          }))
        )
      };

      // Update SSCC status to 'arrived'
      const updatedSSCC = {
        ...ssccData,
        status: 'arrived'
      };

      // Update all ASN statuses in the SSCC to 'arrived'
      updatedSSCC.receiptedEvents = updatedSSCC.receiptedEvents.map((event: any) => ({
        ...event,
        packages: event.packages.map((pkg: any) => ({
          ...pkg,
          status: 'arrived',
          statusDate: dayjs()
        }))
      }));

      await store.ssccs.update(updatedSSCC);

      // Capture after state for audit
      const afterState = {
        manifestId: selectedManifest.id,
        ssccId: selectedSSCC,
        ssccStatus: 'arrived',
        asnStatuses: updatedSSCC.receiptedEvents.flatMap((event: any) =>
          event.packages.map((pkg: any) => ({
            asn: pkg.asn,
            status: pkg.status
          }))
        )
      };

      // Log audit action
      await logAdminAction({
        actionType: AdminActionType.SSCC_ADD_TO_MANIFEST,
        beforeAction: beforeState,
        afterAction: afterState,
        actionBy: user.email,
        reason: addSSCCReason
      });

      alert('SSCC successfully added to manifest', 'success');
      setOpenAddSSCCDialog(false);
      setSelectedSSCC('');
      setAddSSCCReason('');
      setSelectedManifest(null);

      // Show Bustle dialog
      setOpenBustleDialog(true);
    } catch (error) {
      console.error('Failed to add SSCC to manifest:', error);
      alert('An unexpected error occurred', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'staged':
        return 'info';
      case 'in transit':
        return 'warning';
      case 'arrived':
      case 'completed':
        return 'success';
      case 'deleted':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Stack spacing={3} sx={{ px: 3 }}>
      {/* Search Section */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                <SearchIcon sx={{ color: '#1976d2', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                Search Trips
              </Typography>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by Trip Number..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <LocalShippingIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                />
                <TextField
                  type="date"
                  label="Filter by Date"
                  value={selectedDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  size="small"
                  InputLabelProps={{
                    shrink: true
                  }}
                  sx={{
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  placeholder="Search by Manifest Number..."
                  value={manifestSearch}
                  onChange={(e) => handleManifestSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Search by SSCC..."
                  value={ssccSearch}
                  onChange={(e) => handleSsccSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <InventoryIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                />
                <TextField
                  fullWidth
                  placeholder="Search by ASN..."
                  value={asnSearch}
                  onChange={(e) => handleAsnSearchChange(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: <DescriptionIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.03)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white'
                      }
                    }
                  }}
                />
              </Stack>
              {(searchQuery || manifestSearch || asnSearch || ssccSearch || selectedDate) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setManifestSearch('');
                    setAsnSearch('');
                    setSsccSearch('');
                    setSelectedDate('');
                    setCurrentPage(1);
                  }}
                  sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>
                  Clear All Filters
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* TripSheets Section */}
      {filteredTripSheets.length > 0 && (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight={600}>
                Trip Sheets ({filteredTripSheets.length})
              </Typography>
              <Tooltip
                title={
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                      Manifest Status Colors
                    </Typography>
                    <Stack spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '4px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #66bb6a'
                          }}
                        />
                        <Typography variant="caption">Green = Completed</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '4px',
                            backgroundColor: '#fff3e0',
                            border: '1px solid #ff9800'
                          }}
                        />
                        <Typography variant="caption">Orange = In Transit</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '4px',
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #42a5f5'
                          }}
                        />
                        <Typography variant="caption">Blue = Staged</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            border: '1px solid #bdbdbd'
                          }}
                        />
                        <Typography variant="caption">Grey = Other Status</Typography>
                      </Stack>
                    </Stack>
                    <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Management Tools
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Mark as Completed: Manually complete manifests when system/process issues prevent normal
                      completion
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                      • Delete Manifest: Permanently remove duplicate or erroneous manifests
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ mt: 1, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
                      All actions are logged in the audit trail and require a reason
                    </Typography>
                  </Box>
                }
                arrow
                placement="right">
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={hideEmptyTrips} onChange={(e) => setHideEmptyTrips(e.target.checked)} size="small" />
                }
                label={
                  <Typography variant="body2" fontWeight={500} sx={{ color: 'text.secondary' }}>
                    Hide Empty Trips
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyDuplicates}
                    onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={500} sx={{ color: 'text.secondary' }}>
                    Show Only Duplicates
                  </Typography>
                }
              />
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages}
              </Typography>
            </Stack>
          </Stack>
          {paginatedTripSheets.map((trip) => (
            <Accordion
              key={trip.id}
              sx={{
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                mb: 2,
                position: 'relative',
                zIndex: 0
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                    <LocalShippingIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Trip #{trip.reference || trip.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {trip.carrier?.businessName || 'Unknown Carrier'} •{' '}
                      {trip.createdAt ? dayjs(trip.createdAt).format('MMM D, YYYY') : 'N/A'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75, maxWidth: 400 }}>
                    {trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No manifests
                      </Typography>
                    ) : (
                      trip.legs.flatMap((leg) =>
                        leg.manifests.map((manifest) => {
                          const status = manifest.status.toString().toLowerCase();
                          const statusColor =
                            status === 'completed'
                              ? { bg: '#e8f5e9', text: '#2e7d32' }
                              : status === 'in transit'
                                ? { bg: '#fff3e0', text: '#ed6c02' }
                                : status === 'staged'
                                  ? { bg: '#e3f2fd', text: '#0288d1' }
                                  : { bg: '#f5f5f5', text: '#757575' };
                          const isDuplicate = manifestOccurrences.get(manifest.id)! > 1;

                          // Find all trips that contain this manifest
                          const tripsWithThisManifest = isDuplicate
                            ? tripSheets
                                .filter((t) => t.legs.some((l) => l.manifests.some((m) => m.id === manifest.id)))
                                .map((t) => t.reference || t.id)
                            : [];

                          return (
                            <Tooltip
                              key={manifest.id}
                              title={
                                <Box sx={{ py: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ display: 'block', fontSize: '0.75rem' }}>
                                    {manifest.id}
                                  </Typography>
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}>
                                    Status: {manifest.status.toString()}
                                  </Typography>
                                  {isDuplicate && (
                                    <>
                                      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', my: 0.75 }} />
                                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                        <Box component="span" sx={{ fontSize: '0.75rem' }}>
                                          ⚠️
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: '#ff9800', fontWeight: 600, fontSize: '0.7rem' }}>
                                          Duplicate manifest
                                        </Typography>
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.8 }}>
                                        Also in: {tripsWithThisManifest.join(', ')}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                              }
                              arrow>
                              <Chip
                                label={manifest.id}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  fontFamily: 'monospace',
                                  backgroundColor: statusColor.bg,
                                  color: statusColor.text,
                                  border: isDuplicate ? '2px solid #ff9800' : 'none',
                                  cursor: 'pointer'
                                }}
                              />
                            </Tooltip>
                          );
                        })
                      )
                    )}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  backgroundColor: '#f5f5f5',
                  p: 2
                }}>
                {trip.legs.reduce((sum, leg) => sum + leg.manifests.length, 0) === 0 ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 5,
                      px: 3,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      border: '1px dashed rgba(0,0,0,0.12)'
                    }}>
                    <InventoryIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.26)', mb: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, color: 'text.primary' }}>
                      No Manifests
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This trip doesn't have any manifests associated with it
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    {trip.legs.map((leg) =>
                      leg.manifests.map((manifest) => {
                        const transformedManifest = transformManifest(manifest, trip, leg);
                        const statusColor =
                          manifest.status.toString().toLowerCase() === 'completed'
                            ? '#2e7d32'
                            : manifest.status.toString().toLowerCase() === 'in transit'
                              ? '#ed6c02'
                              : manifest.status.toString().toLowerCase() === 'staged'
                                ? '#0288d1'
                                : '#757575';
                        const isDuplicate = manifestOccurrences.get(manifest.id)! > 1;
                        const duplicateCount = manifestOccurrences.get(manifest.id)!;

                        return (
                          <Box
                            key={manifest.id}
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              backgroundColor: 'white',
                              border: isDuplicate ? '2px solid #ff9800' : '1px solid rgba(0,0,0,0.12)',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                              transition: 'all 0.2s',
                              '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                borderColor: 'rgba(0,0,0,0.2)'
                              }
                            }}>
                            {/* Duplicate Warning Banner */}
                            {isDuplicate && (
                              <Box
                                sx={{
                                  backgroundColor: '#fff3e0',
                                  borderBottom: '1px solid #ffb74d',
                                  px: 2.5,
                                  py: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                <ErrorOutlineIcon sx={{ fontSize: 16, color: '#f57c00' }} />
                                <Typography variant="caption" fontWeight={600} sx={{ color: '#e65100' }}>
                                  DUPLICATE: This manifest appears on {duplicateCount} trips
                                </Typography>
                              </Box>
                            )}

                            {/* Clean Header */}
                            <Box
                              sx={{
                                px: 3,
                                py: 2.5,
                                backgroundColor: isDuplicate ? 'rgba(255, 152, 0, 0.02)' : 'transparent',
                                borderBottom: '1px solid rgba(0,0,0,0.08)'
                              }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Box>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                                      {manifest.id}
                                    </Typography>
                                    {isDuplicate && (
                                      <Chip
                                        label={`${duplicateCount}x`}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#ff9800',
                                          color: 'white',
                                          fontWeight: 700,
                                          height: 18,
                                          fontSize: '0.7rem'
                                        }}
                                      />
                                    )}
                                  </Stack>
                                  <Typography variant="body2" color="text.secondary">
                                    {leg.destination.businessName} • Trailer: {manifest.trailerId || 'N/A'}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={manifest.status.toString()}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      manifest.status.toString().toLowerCase() === 'completed'
                                        ? '#e8f5e9'
                                        : manifest.status.toString().toLowerCase() === 'in transit'
                                          ? '#fff3e0'
                                          : manifest.status.toString().toLowerCase() === 'staged'
                                            ? '#e3f2fd'
                                            : '#f5f5f5',
                                    color: statusColor,
                                    fontWeight: 600,
                                    border: 'none'
                                  }}
                                />
                              </Stack>
                            </Box>

                            {/* Content */}
                            <Box sx={{ p: 3, backgroundColor: '#fafafa' }}>
                              <Stack spacing={2.5}>
                                {/* SSCCs & ASNs in Grid */}
                                <Stack direction="row" spacing={3}>
                                  {/* SSCCs */}
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                      <InventoryIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="body2" fontWeight={600} color="text.primary">
                                        SSCCs
                                      </Typography>
                                      <Chip
                                        label={transformedManifest.ssccs.length}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.7rem',
                                          backgroundColor: 'rgba(0,0,0,0.06)',
                                          color: 'text.secondary',
                                          fontWeight: 600
                                        }}
                                      />
                                    </Stack>
                                    <Box
                                      sx={{
                                        maxHeight: 120,
                                        overflowY: 'auto',
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        p: 1.5,
                                        '&::-webkit-scrollbar': { width: 6 },
                                        '&::-webkit-scrollbar-thumb': {
                                          backgroundColor: 'rgba(0,0,0,0.2)',
                                          borderRadius: 3
                                        }
                                      }}>
                                      <Stack spacing={0.75}>
                                        {transformedManifest.ssccs.map((sscc) => (
                                          <Typography
                                            key={sscc}
                                            variant="body2"
                                            sx={{
                                              fontFamily: 'monospace',
                                              color: 'text.primary',
                                              fontSize: '0.85rem'
                                            }}>
                                            {sscc}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </Box>
                                  </Box>

                                  {/* ASNs */}
                                  <Box sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                                      <DescriptionIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="body2" fontWeight={600} color="text.primary">
                                        ASNs
                                      </Typography>
                                      <Chip
                                        label={transformedManifest.asns.length}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: '0.7rem',
                                          backgroundColor: 'rgba(0,0,0,0.06)',
                                          color: 'text.secondary',
                                          fontWeight: 600
                                        }}
                                      />
                                    </Stack>
                                    <Box
                                      sx={{
                                        maxHeight: 120,
                                        overflowY: 'auto',
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        p: 1.5,
                                        '&::-webkit-scrollbar': { width: 6 },
                                        '&::-webkit-scrollbar-thumb': {
                                          backgroundColor: 'rgba(0,0,0,0.2)',
                                          borderRadius: 3
                                        }
                                      }}>
                                      <Stack spacing={0.75}>
                                        {transformedManifest.asns.map((asn) => (
                                          <Typography
                                            key={asn}
                                            variant="body2"
                                            sx={{
                                              fontFamily: 'monospace',
                                              color: 'text.primary',
                                              fontSize: '0.85rem'
                                            }}>
                                            {asn}
                                          </Typography>
                                        ))}
                                      </Stack>
                                    </Box>
                                  </Box>
                                </Stack>

                                <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />

                                {/* Admin Actions */}
                                <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => handleOpenAddSSCCDialog(transformedManifest)}
                                    sx={{
                                      px: 2,
                                      py: 0.75,
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      textTransform: 'none',
                                      borderColor: 'rgba(25, 118, 210, 0.5)',
                                      color: '#1976d2',
                                      borderRadius: 1,
                                      '&:hover': {
                                        borderColor: '#1976d2',
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                      }
                                    }}>
                                    Add SSCC
                                  </Button>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => {
                                      setSelectedManifest(transformedManifest);
                                      setOpenCompleteDialog(true);
                                    }}
                                    disabled={manifest.status.toString().toLowerCase() === 'completed'}
                                    sx={{
                                      px: 2,
                                      py: 0.75,
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      textTransform: 'none',
                                      backgroundColor: '#4caf50',
                                      '&:hover': { backgroundColor: '#388e3c' },
                                      '&:disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' },
                                      boxShadow: 'none',
                                      borderRadius: 1
                                    }}>
                                    Mark Completed
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => {
                                      setSelectedManifest(transformedManifest);
                                      setValidationError('');
                                      setOpenDeleteDialog(true);
                                    }}
                                    sx={{
                                      px: 2,
                                      py: 0.75,
                                      fontWeight: 500,
                                      fontSize: '0.8rem',
                                      textTransform: 'none',
                                      borderColor: 'rgba(211, 47, 47, 0.5)',
                                      color: '#d32f2f',
                                      borderRadius: 1,
                                      '&:hover': {
                                        borderColor: '#d32f2f',
                                        backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                      }
                                    }}>
                                    Delete
                                  </Button>
                                </Stack>
                              </Stack>
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
            </Stack>
          )}
        </Stack>
      )}

      {/* No Results */}
      {filteredTripSheets.length === 0 && (searchQuery || manifestSearch || asnSearch || selectedDate) && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalShippingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No trip sheets found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {manifestSearch
              ? 'No trips found containing that manifest number'
              : asnSearch
                ? 'No trips found containing that ASN'
                : searchQuery && selectedDate
                  ? 'Try adjusting your trip number or date filters'
                  : searchQuery
                    ? 'Try searching with a different trip number'
                    : 'Try selecting a different date'}
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {tripSheets.length === 0 && !searchQuery && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading trip sheets...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Syncing data from DataStore
          </Typography>
        </Box>
      )}

      {/* Empty State */}
      {tripSheets.length > 0 && filteredTripSheets.length === 0 && !searchQuery && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <LocalShippingIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No trip sheets available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Trip sheets will appear here once they are created
          </Typography>
        </Box>
      )}

      {/* Mark as Completed Dialog */}
      <Dialog
        open={openCompleteDialog}
        onClose={() => {
          setOpenCompleteDialog(false);
          setActionReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#f9fafb'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'white',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <CheckCircleIcon sx={{ fontSize: 22, color: '#4caf50' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Mark Manifest as Completed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Manually complete this manifest
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<ErrorOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(2, 136, 209, 0.2)'
              }}>
              Use this action when process or system issues prevent standard completion of the manifest.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {selectedManifest?.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedManifest?.status}
                      color={getStatusColor(selectedManifest?.status || '')}
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Completion *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Explain why this manifest needs to be manually marked as completed (e.g., system error, process issue, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenCompleteDialog(false);
              setActionReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleMarkAsCompleted}
            disabled={!actionReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#388e3c' },
              boxShadow: 'none',
              borderRadius: 1
            }}>
            {isProcessing ? 'Processing...' : 'Mark Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add SSCC to Manifest Dialog */}
      <Dialog
        open={openAddSSCCDialog}
        onClose={() => {
          setOpenAddSSCCDialog(false);
          setSelectedSSCC('');
          setAddSSCCReason('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#f9fafb'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'white',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <AddIcon sx={{ fontSize: 22, color: '#1976d2' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Add SSCC to Manifest
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Add an SSCC that was received without a manifest
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(2, 136, 209, 0.2)'
              }}>
              Adding this SSCC will update its status and all ASN statuses to 'arrived'.
            </Alert>

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {selectedManifest?.id}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Select SSCC *
              </Typography>
              <Autocomplete
                options={availableSSCCs}
                getOptionLabel={(option) => `${option.id} - ${option.status || 'Unknown'}`}
                value={availableSSCCs.find((s) => s.id === selectedSSCC) || null}
                onChange={(_, newValue) => setSelectedSSCC(newValue?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for SSCC..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          display: 'flex'
                        }}>
                        <InventoryIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.destinationLocation?.name || 'Unknown destination'} • {option.status || 'Unknown'}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
              />
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Adding *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={addSSCCReason}
                onChange={(e) => setAddSSCCReason(e.target.value)}
                placeholder="Explain why this SSCC is being added to the manifest (e.g., received without manifest, system error, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenAddSSCCDialog(false);
              setSelectedSSCC('');
              setAddSSCCReason('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleAddSSCCToManifest}
            disabled={!selectedSSCC || !addSSCCReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' },
              boxShadow: 'none',
              borderRadius: 1
            }}>
            {isProcessing ? 'Adding...' : 'Add SSCC'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bustle Confirmation Dialog */}
      <Dialog
        open={openBustleDialog}
        onClose={() => setOpenBustleDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fff3e0'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'white',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <ErrorOutlineIcon sx={{ fontSize: 22, color: '#ff9800' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Action Required
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Alert severity="warning" sx={{ borderRadius: 1.5 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Access Bustle and Cancel Job
            </Typography>
            <Typography variant="body2">
              Please access Bustle to cancel any related jobs for this SSCC addition.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            variant="contained"
            onClick={() => setOpenBustleDialog(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: '#ff9800',
              '&:hover': { backgroundColor: '#f57c00' }
            }}>
            Understood
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Manifest Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setActionReason('');
          setValidationError('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}>
        <DialogTitle
          sx={{
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            px: 3,
            py: 2.5,
            backgroundColor: '#fef6f6'
          }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                backgroundColor: 'white',
                display: 'flex',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
              <DeleteIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Delete Manifest
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Permanently remove this manifest
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <Alert
              severity="warning"
              icon={<ErrorOutlineIcon />}
              sx={{
                borderRadius: 1.5,
                border: '1px solid rgba(237, 108, 2, 0.2)'
              }}>
              This action will permanently mark the manifest as deleted. This action cannot be undone.
            </Alert>

            {validationError && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 1.5,
                  border: '1px solid rgba(211, 47, 47, 0.2)'
                }}>
                {validationError}
              </Alert>
            )}

            <Box
              sx={{
                p: 2.5,
                borderRadius: 1.5,
                backgroundColor: '#fafafa',
                border: '1px solid rgba(0,0,0,0.08)'
              }}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Manifest ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                    {selectedManifest?.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}>
                    Current Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedManifest?.status}
                      color={getStatusColor(selectedManifest?.status || '')}
                      size="small"
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                  mb: 1.5,
                  display: 'block'
                }}>
                Reason for Deletion *
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                required
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Explain why this manifest is being deleted (e.g., duplicate, erroneous entry, etc.)..."
                helperText="This reason will be logged in the audit trail"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: '#fafafa',
            borderTop: '1px solid rgba(0,0,0,0.08)'
          }}>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setActionReason('');
              setValidationError('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary'
            }}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDeleteManifest}
            disabled={!actionReason.trim() || isProcessing}
            startIcon={
              isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              px: 2,
              py: 0.75,
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              borderColor: 'rgba(211, 47, 47, 0.5)',
              color: '#d32f2f',
              borderRadius: 1,
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.04)'
              }
            }}>
            {isProcessing ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
